/**
 * Global product capability codes (seeded in permissions table).
 * Prefer these over free-text permission invent.
 */
export enum GlobalCapability {
  UsersManage = 'users.manage',
  UsersResetMfa = 'users.reset_mfa',
  RolesManage = 'roles.manage',
  TagsManage = 'tags.manage',
  FormsConfigure = 'forms.configure',
  /** Backend-only / legacy; not exposed in Access UI for MACE */
  SettingsManage = 'settings.manage'
}

/** Global codes shown and toggled in Manejo de accesos. */
export const GLOBAL_CAPABILITY_CODES: string[] = [
  GlobalCapability.UsersManage,
  GlobalCapability.UsersResetMfa,
  GlobalCapability.RolesManage,
  GlobalCapability.TagsManage,
  GlobalCapability.FormsConfigure
];

/** Family names stored in role_form_grant_actions.family */
export type FormGrantFamily =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'comment'
  | 'view_identity'
  | 'export'
  | 'analysis'
  | 'publish';

export type FormGrantScope = 'own' | 'any';

/** Expanded form grant action codes (API payload). */
export type FormGrantAction =
  | 'view_own'
  | 'view_any'
  | 'create_own'
  | 'create_any'
  | 'edit_own'
  | 'edit_any'
  | 'delete_own'
  | 'delete_any'
  | 'comment_own'
  | 'comment_any'
  | 'view_identity_own'
  | 'view_identity_any'
  | 'export_own'
  | 'export_any'
  | 'analysis_own'
  | 'analysis_any'
  | 'publish_own'
  | 'publish_any'
  // Short aliases still accepted by CanForm / backend expand
  | 'view'
  | 'create'
  | 'delete'
  | 'comment'
  | 'view_identity'
  | 'export'
  | 'analysis';

export const FORM_GRANT_FAMILIES: FormGrantFamily[] = [
  'view',
  'create',
  'edit',
  'delete',
  'comment',
  'view_identity',
  'export',
  'analysis',
  'publish'
];

/** Expanded own/any codes for every family. */
export const FORM_GRANT_ACTIONS: FormGrantAction[] = FORM_GRANT_FAMILIES.reduce(
  (acc, family) => {
    acc.push(`${family}_own` as FormGrantAction, `${family}_any` as FormGrantAction);
    return acc;
  },
  [] as FormGrantAction[]
);

/**
 * Report-detail comments (public + structured post comments).
 * Off for now: product asked not to solicit comments on a report.
 * Set true to restore UI, access grant chip, and API usage.
 */
export const FEATURE_POST_COMMENTS = false;

export const FORM_GRANT_FAMILIES_UI: FormGrantFamily[] = FORM_GRANT_FAMILIES.filter(
  f => f !== 'comment' || FEATURE_POST_COMMENTS
);

/** Actions shown in Access Management UI (respects feature flags). */
export const FORM_GRANT_ACTIONS_UI: FormGrantAction[] = FORM_GRANT_ACTIONS.filter(
  a => !a.startsWith('comment_') || FEATURE_POST_COMMENTS
);

export interface FormGrant {
  form_id: number | null;
  all_forms: boolean;
  actions: FormGrantAction[];
}

/** Own/any UI row metadata. */
export interface FormScopeFamily {
  key: FormGrantFamily;
  titleKey: string;
  hintKey: string;
  own: FormGrantAction;
  any: FormGrantAction;
}

export const FORM_SCOPE_FAMILIES: FormScopeFamily[] = FORM_GRANT_FAMILIES_UI.map(
  family => ({
    key: family,
    titleKey: `FORM_SCOPE_${family.toUpperCase()}`,
    hintKey: `FORM_SCOPE_${family.toUpperCase()}_HINT`,
    own: `${family}_own` as FormGrantAction,
    any: `${family}_any` as FormGrantAction
  })
);

/** Expand short action to concrete codes for matching. */
export function expandFormGrantAction(action: FormGrantAction | string): string[] {
  const a = String(action);
  if (a.endsWith('_own') || a.endsWith('_any')) {
    return [a];
  }
  if (FORM_GRANT_FAMILIES.includes(a as FormGrantFamily)) {
    return [`${a}_own`, `${a}_any`];
  }
  return [a];
}

/** CE = form 1, OE = form 2 (Perú defaults). */
export const LEGACY_FORM_IDS = {
  conflictividadElectoral: 1,
  operacionesElectorales: 2
} as const;
